/**
 * Centralized utilities for plan limit handling.
 * The backend uses -1 to represent "unlimited" for maxUsers, maxExpensesPerMonth, etc.
 * This module provides helpers so we don't scatter -1 checks across the codebase.
 */

export const UNLIMITED = -1;

/** Check if a limit value represents unlimited */
export const isUnlimited = (value: number): boolean => value === UNLIMITED;

/** Format a limit value for display: -1 → "Ilimitado", otherwise the number */
export const formatLimit = (value: number, suffix?: string): string => {
  if (isUnlimited(value)) return 'Ilimitado';
  return suffix ? `${value} ${suffix}` : value.toLocaleString('es-CL');
};

/** Format with plural variant: -1 → "Ilimitados" */
export const formatLimitPlural = (value: number): string => {
  if (isUnlimited(value)) return 'Ilimitados';
  return value.toLocaleString('es-CL');
};

/** Check if usage is at the limit (never true for unlimited) */
export const isAtLimit = (current: number, limit: number): boolean => {
  if (isUnlimited(limit)) return false;
  return current >= limit;
};

/** Check if usage has a warning (never true for unlimited) */
export const hasLimitWarning = (percentage: number, limit: number, threshold = 80): boolean => {
  if (isUnlimited(limit)) return false;
  return percentage >= threshold;
};

/** Get available slots (returns Infinity for unlimited) */
export const getAvailableSlots = (current: number, limit: number): number => {
  if (isUnlimited(limit)) return Infinity;
  return limit - current;
};

/** Get usage percentage (returns 0 for unlimited) */
export const getUsagePercentage = (current: number, limit: number): number => {
  if (isUnlimited(limit)) return 0;
  if (limit === 0) return 0;
  return Math.round((current / limit) * 100);
};
